import React, { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, message } from "@mimber/ui-web"
import { Form, SelectField, DatePickerField } from "@mimber/forms"
import {
  useRoles,
  useMemberRoles,
  useAssignRole,
} from "@mimber/client/mimber-organization"
import { type CreateRoleAssignmentDto } from "@mimber/schema"
import { z } from "zod"
import type { AssignRoleToMemberFormProps } from "./AssignRoleToMemberForm.types"

const AssignRoleToMemberFormSchema = z.object({
  roleUuid: z.string().uuid(),
  expiresAt: z.string().datetime().optional(),
})

type AssignRoleToMemberFormValues = z.infer<typeof AssignRoleToMemberFormSchema>

export const AssignRoleToMemberForm: React.FunctionComponent<
  AssignRoleToMemberFormProps
> = ({ memberUuid, onSuccess, onCancel }) => {
  const [selectedRoleUuid, setSelectedRoleUuid] = useState<string>("")

  const form = useForm<AssignRoleToMemberFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(AssignRoleToMemberFormSchema) as any,
    mode: "onBlur",
    defaultValues: {
      roleUuid: "",
      expiresAt: undefined,
    },
  })

  const {
    formState: {},
    reset,
    watch,
  } = form

  const watchedRoleUuid = watch("roleUuid")
  React.useEffect(() => {
    setSelectedRoleUuid(watchedRoleUuid)
  }, [watchedRoleUuid])

  const { data: rolesResponse, isLoading: rolesLoading } = useRoles()
  const { data: memberRolesResponse } = useMemberRoles(memberUuid)

  const roleOptions = useMemo(() => {
    if (!rolesResponse?.data) return []
    const assignedRoleUuids = new Set(
      (memberRolesResponse?.data?.directRoles ?? []).map(
        (a) => a.organizationRole.uuid
      )
    )
    return rolesResponse.data
      .filter((role) => !assignedRoleUuids.has(role.uuid))
      .map((role) => ({
        label: role.name,
        value: role.uuid,
      }))
  }, [rolesResponse, memberRolesResponse])

  const assignRole = useAssignRole(selectedRoleUuid, {
    onSuccess: () => {
      message.success("Role assigned to member successfully")
      reset()
      onSuccess?.()
    },
    onError: (err) => {
      message.error(`Failed to assign role: ${err.message}`)
    },
  })

  const onSubmit = (data: AssignRoleToMemberFormValues) => {
    if (!data.roleUuid) return
    const dto: CreateRoleAssignmentDto = {
      membershipUuid: memberUuid,
      expiresAt: data.expiresAt,
    }
    assignRole.mutate(dto)
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={assignRole.isPending}
      layout="vertical"
    >
      <SelectField
        name="roleUuid"
        label="Role"
        placeholder="Select a role"
        required
        options={roleOptions}
        loading={rolesLoading}
        showSearch
        filterOption={(input, option) =>
          String(option?.label ?? "")
            .toLowerCase()
            .includes(input.toLowerCase())
        }
      />

      <DatePickerField
        name="expiresAt"
        label="Expires At"
        help="Optional — leave blank for no expiration"
      />

      <Space style={{ width: "100%", justifyContent: "flex-end" }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={assignRole.isPending}>
            Cancel
          </Button>
        )}
        <Button type="primary" htmlType="submit" loading={assignRole.isPending}>
          Assign
        </Button>
      </Space>
    </Form>
  )
}
